// Guided tours and tooltips for the Tutor profile only (ARH Guide, synced into ./guide from
// D:/ARH-OS/ARH-PROJECTS/arh-guide; do not edit the copy). Other tenants get no attributes, no
// script and no styles: tip() returns nothing and loadGuide() returns null.
// Copy rule: facts here match the Tutor crash course (arh-urus-kelas docs/tutor-course, chapter 3).
import type { Guide, GuideTour } from './guide/arh-guide.js';
import { tenant } from './tenant';

export const guideOn = tenant.id === 'tutor';
export type TourId = 'workspace' | 'call';

/** Spread onto an element: {...tip('Mute or unmute')} — empty outside the Tutor profile. */
export function tip(text: string): { 'data-tip'?: string } {
  return guideOn ? { 'data-tip': text } : {};
}

const course = { href: new URL('/course/index.html#module-3', tenant.homeUrl ?? location.origin).href, label: 'Learn more: crash course chapter 3, teach a lesson →' };

const tours: Record<TourId, GuideTour> = {
  workspace: {
    steps: [
      { title: 'Your session workspace', body: 'Files for this lesson live here before, during and after the call. Opening this page does not start a call.' },
      { el: '[data-testid="file-search"]', title: 'Search every folder', body: 'Find a worksheet by name across all folders.' },
      { el: '[data-testid="dropzone"]', title: 'Add files', body: 'Drop files or whole folders here. Uploading a file with the same name keeps the old one as a version.' },
      { el: '[data-testid="new-folder"]', title: 'Folders per lesson', body: 'Make a folder for each week or topic so files stay easy to find.' },
      { el: '[data-testid="room-panel"]', title: 'The lesson link', body: 'Turn on the room for one link that stays the same. Pupils open it whenever there is a lesson.' },
      { el: '[data-testid="start-session"]', title: 'Start the call', body: 'Starts the live lesson. Pupils waiting on the link join you.' },
    ],
    more: course,
  },
  call: {
    steps: [
      { title: 'You are live', body: 'A quick look at the controls. Everyone stays connected while this guide is open.' },
      { el: '.dock .ctl-wrap:has(> button[aria-label^="Mic"])', title: 'Microphone', body: 'Red means muted. Tap to speak.' },
      { el: '.dock .ctl-wrap:has(> button[aria-label^="Camera"])', title: 'Camera', body: 'Turn your camera on or off.' },
      { el: '.dock .ctl-wrap:has(> button[aria-label^="Screen"])', title: 'Share your screen', body: 'Show a worksheet or slides. On iPhone and iPad, screen sharing may not be offered.' },
      { el: '[data-testid="files-button"]', title: 'Present a worksheet', body: 'Open FILES and tap Present on a PDF or picture. Pupils follow your page, and anyone who joins late opens on it.' },
      { el: '[data-testid="guests-button"]', title: 'Who is here', body: 'See pupils in the call, let in anyone waiting, and mute one pupil or everyone. A pupil whose connection drops is marked on their tile.' },
      { el: '.dock .ctl.end', title: 'End for everyone', body: 'Tap twice to end the lesson for all. Leave only takes you out.' },
    ],
    more: course,
  },
};

const listeners = new Set<() => void>();
export function onGuideChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

let ready: Promise<Guide> | null = null;
/** Loads ARH Guide once (tips on from then), or null outside the Tutor profile. */
export function loadGuide(): Promise<Guide> | null {
  if (!guideOn) return null;
  ready ??= Promise.all([import('./guide/vendor/driver.js.mjs'), import('./guide/arh-guide.js'), import('./guide/vendor/driver.css'), import('./guide/arh-guide.css')]).then(
    ([{ driver }, g]) => {
      g.enableTips();
      return g.createGuide({ driver, app: 'urus-tutor-video', tours, onEvent: (e) => e.type !== 'start' && listeners.forEach((fn) => fn()) });
    },
  );
  return ready;
}
